import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  NotificationsOutlined as MessagesIcon,
} from "@mui/icons-material";
import {useEffect, useState} from "react";
import SendAdminMessageDialog from "../componenets/messages/SendAdminMessageDialog";
import ConfirmDialog from "../componenets/ui/ConfirmDialog";
import {
  deleteSentAdminMessage,
  getSentAdminMessages,
} from "../services/AdminMessageService";
import {useMessageContext} from "../contexts/MessageContext";
import {t} from "../componenets/dashboard/coach/coachTokens";
import moment from "moment";
import "moment/locale/he";

moment.locale("he");

/* ── Table cell presets (match Clients / Companies) ── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

const AdminMessages = () => {
  const {notificationHandler} = useMessageContext();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const reload = () => {
    setLoading(true);
    getSentAdminMessages()
      .then(res => setMessages(res.data || []))
      .catch(() => notificationHandler.error("טעינת ההודעות נכשלה"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = (e) => {
    if (e) e.stopPropagation();
    if (!confirmDeleteId) return;
    deleteSentAdminMessage(confirmDeleteId)
      .then(() => {
        notificationHandler.success("ההודעה נמחקה");
        setConfirmDeleteId(null);
        reload();
      })
      .catch(() => notificationHandler.error("מחיקה נכשלה"));
  };

  const formatRecipients = (m) =>
    m.targetAllCompanies ? "כל החברות" : `${m.targetCompanyCount} חברות`;

  return (
    <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
      <SendAdminMessageDialog
        isOpen={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={reload}
      />

      {/* ─── Title row (on canvas) ─── */}
      <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
        <Box>
          <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
            הודעות מערכת
          </Typography>
          <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>שליחת הודעות לחברות</Typography>
        </Box>
        <Box component="button" onClick={() => setComposeOpen(true)} sx={{
          display: "inline-flex", alignItems: "center", gap: "5px",
          background: t.green, color: "#fff",
          height: 34, px: "15px", borderRadius: t.rs,
          fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
          border: "none", cursor: "pointer", fontFamily: "inherit",
          transition: "all .14s",
          "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
        }}>
          <AddIcon sx={{fontSize: 16}}/>
          הודעה חדשה
        </Box>
      </Box>

      {/* ─── Table card ─── */}
      <Box sx={{
        background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
        boxShadow: t.sh, overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>

        {/* Filter bar (count pill) */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: "7px",
          padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
          background: t.bg, flexWrap: "wrap", flexShrink: 0, direction: "rtl",
        }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            fontSize: "12px", fontWeight: 700, color: t.greenText,
            background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
            padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
          }}>
            <MessagesIcon sx={{fontSize: 14}}/>
            {messages.length} הודעות
          </Box>
        </Box>

        {/* Table area */}
        {loading && !messages.length ? (
          <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540}}>
            <CircularProgress size={32} sx={{color: t.green}}/>
          </Box>
        ) : !messages.length ? (
          <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
            <MessagesIcon sx={{fontSize: 48, color: t.border}}/>
            <Typography sx={{color: t.t3, fontSize: "15px", fontWeight: 500}}>אין הודעות שנשלחו</Typography>
            <Typography sx={{color: t.t3, fontSize: "13px"}}>לחץ על "הודעה חדשה" כדי לשלוח</Typography>
          </Box>
        ) : (
          <TableContainer sx={{overflowY: "auto", maxHeight: 600, direction: "rtl"}}>
            <Table stickyHeader sx={{tableLayout: "fixed"}}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{...th, textAlign: "right"}}>כותרת</TableCell>
                  <TableCell sx={{...th, textAlign: "right", width: "18%", display: {xs: "none", md: "table-cell"}}}>נמענים</TableCell>
                  <TableCell sx={{...th, textAlign: "center", width: "12%", display: {xs: "none", md: "table-cell"}}}>נשלח ל-</TableCell>
                  <TableCell sx={{...th, textAlign: "center", width: "14%", display: {xs: "none", md: "table-cell"}}}>תאריך</TableCell>
                  <TableCell sx={{...th, textAlign: "center", width: "8%"}}>פעולות</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.map((m) => (
                  <TableRow key={m.id} sx={{"&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}>
                    <TableCell sx={{...td, textAlign: "right"}}>
                      <Typography sx={{fontSize: "12.5px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        {m.title}
                      </Typography>
                      <Typography sx={{fontSize: "11.5px", color: t.t2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        {m.content}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                      <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: t.t2}}>
                        {formatRecipients(m)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                      <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: t.t2}}>
                        {m.recipientCount}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                      <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: t.t2}}>
                        {m.createdDate ? moment.unix(m.createdDate).format("DD/MM/YY HH:mm") : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{...td, textAlign: "center"}}>
                      <Tooltip title="מחק">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(m.id); }}
                          sx={{color: t.red, "&:hover": {bgcolor: t.redLight}}}
                        >
                          <DeleteIcon fontSize="small"/>
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <ConfirmDialog
        open={Boolean(confirmDeleteId)}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleDelete}
        title="מחיקת הודעה"
        description="ההודעה תוסר מתיבת הדואר של כל הנמענים. לא ניתן לבטל פעולה זו."
        confirmText="מחק"
        cancelText="בטל"
      />
    </Box>
  );
};

export default AdminMessages;
